import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardBody, Button, Badge } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { Bell, Plus, Trash2, Mail, ExternalLink, Send } from 'lucide-react';
import { formatDate, formatDateTime } from '@/lib/format';
import {
  useServiceNotifications,
  useServiceMailerTracking,
  type ServiceNotification,
  type ServiceMailerTracking,
} from '@/api/services';
import { useDeleteServiceNotification } from '@/api/services-mutations';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { ServiceNotificationDialog } from './service-notification-dialog';
import { ServiceNotificationSendDialog } from './service-notification-send-dialog';

const muted = { color: 'var(--vn-fg-muted)', fontStyle: 'italic' } as const;

/**
 * Notification tab — mirrors legacy "Thông báo dịch vụ": a technical-notification
 * records table (name / DNS / details / documentation / attachments) with compose
 * + delete, over an "Email history" tracking table (`/technical/notifications`).
 */
export function ServiceNotificationTab({ id }: { id: number }) {
  const toast = useToast();
  const notifications = useServiceNotifications(id);
  const tracking = useServiceMailerTracking(id);
  const remove = useDeleteServiceNotification(id);
  const [toDelete, setToDelete] = useState<ServiceNotification | null>(null);
  const [toSend, setToSend] = useState<ServiceNotification | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);

  if (notifications.isError) return <TabError title="Failed to load notifications" onRetry={() => notifications.refetch()} />;
  if (notifications.isLoading) return <TabLoading rows={4} />;

  const rows = notifications.data?.results ?? [];
  const trackRows = tracking.data?.results ?? [];

  const columns: ColumnDef<ServiceNotification, unknown>[] = [
    { id: 'notification_name', header: 'Notification / Mailer', cell: ({ row }) => row.original.notification_name || `#${row.original.service_system_information_id}` },
    {
      id: 'dns',
      header: 'DNS',
      cell: ({ row }) => {
        const n = row.original.dns_managements?.length ?? 0;
        return row.original.dns_enable || n ? <StatusPill tone="success">{n ? `${n} record${n > 1 ? 's' : ''}` : 'Enabled'}</StatusPill> : <span style={muted}>—</span>;
      },
    },
    {
      id: 'details',
      header: 'Details',
      cell: ({ row }) => {
        const m = row.original.metadata?.filter((x) => x?.key) ?? [];
        return m.length ? <span>{m.map((x) => x.key).join(', ')}</span> : <span style={muted}>—</span>;
      },
    },
    {
      id: 'documentation',
      header: 'Documentation',
      cell: ({ row }) => {
        const docs = row.original.documentations?.filter((d) => d?.link) ?? [];
        return docs.length ? (
          <span style={{ display: 'flex', flexDirection: 'column', gap: 'var(--vn-space-1)' }}>
            {docs.slice(0, 3).map((d, i) => (
              <a key={i} href={d.link} target="_blank" rel="noreferrer" style={{ color: 'var(--vn-primary-text)', display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)' }}>
                {d.name || d.link} <ExternalLink size={12} />
              </a>
            ))}
          </span>
        ) : <span style={muted}>—</span>;
      },
    },
    {
      id: 'attachments',
      header: 'Attachments',
      cell: ({ row }) => {
        const n = row.original.attachments?.length ?? 0;
        return n ? <Badge tone="neutral" appearance="subtle" size="sm">{n} file{n > 1 ? 's' : ''}</Badge> : <span style={muted}>—</span>;
      },
    },
    { id: 'created_at', header: 'Created', cell: ({ row }) => formatDate(row.original.created_at) },
    {
      id: 'actions',
      header: () => <span style={{ display: 'block', textAlign: 'right' }}>Actions</span>,
      cell: ({ row }) => (
        <span style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--vn-space-1)' }}>
          <Button size="sm" variant="ghost" aria-label="Send email" onClick={() => setToSend(row.original)}>
            <Send size={16} />
          </Button>
          <Button size="sm" variant="ghost" aria-label="Delete" onClick={() => setToDelete(row.original)}>
            <Trash2 size={16} />
          </Button>
        </span>
      ),
    },
  ];

  const trackColumns: ColumnDef<ServiceMailerTracking, unknown>[] = [
    { id: 'task_jid', header: 'Task JID', cell: ({ row }) => row.original.task_jid || `#${row.original.task_id}` },
    { id: 'subject', header: 'Subject', cell: ({ row }) => row.original.metadata?.subject || <span style={muted}>—</span> },
    { id: 'mailer', header: 'Notification / Mailer', cell: ({ row }) => row.original.metadata?.metadata?.mailer?.customer_mailer_name || row.original.metadata?.metadata?.notification?.notification_name || <span style={muted}>—</span> },
    { id: 'recipient', header: 'Recipient', cell: ({ row }) => row.original.metadata?.metadata?.mailer?.mailer_receiver || <span style={muted}>—</span> },
    { id: 'cc', header: 'CC', cell: ({ row }) => row.original.metadata?.metadata?.mailer?.mailer_cc || <span style={muted}>—</span> },
    { id: 'sender', header: 'Sender', cell: ({ row }) => row.original.metadata?.metadata?.sender?.employee_name || row.original.metadata?.sender?.employee_name || <span style={muted}>—</span> },
    { id: 'created', header: 'Created', cell: ({ row }) => formatDateTime(row.original.task_created_at) },
  ];

  const confirmDelete = () => {
    if (!toDelete) return;
    remove.mutate(toDelete.service_system_information_id, {
      onSuccess: () => { toast.success('Notification deleted'); setToDelete(null); },
      onError: (err) => toast.error('Failed to delete notification', { description: err instanceof Error ? err.message : undefined }),
    });
  };

  return (
    <>
      <Card flat>
        <CardHeader
          actions={
            <Button size="sm" variant="secondary" onClick={() => setComposeOpen(true)}>
              <Plus size={16} /> Add notification
            </Button>
          }
        >
          <CardTitle>Notification configs</CardTitle>
        </CardHeader>
        <CardBody>
          <DataTable
            columns={columns}
            data={rows}
            getRowId={(r) => String(r.service_system_information_id)}
            emptyState={<EmptyState icon={<Bell size={24} />} title="No notifications" description="No technical notifications have been created for this service." />}
          />
        </CardBody>
      </Card>

      <Card flat>
        <CardHeader>
          <CardTitle>Email history</CardTitle>
        </CardHeader>
        <CardBody>
          <DataTable
            columns={trackColumns}
            data={trackRows}
            getRowId={(r) => String(r.task_id)}
            emptyState={<EmptyState icon={<Mail size={24} />} title="No emails sent" description="No notification emails have been sent for this service yet." />}
          />
        </CardBody>
      </Card>

      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(o) => (o ? undefined : setToDelete(null))}
        title="Delete notification"
        description="This removes the notification record. This action cannot be undone."
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={confirmDelete}
      />

      <ServiceNotificationDialog serviceId={id} open={composeOpen} onOpenChange={setComposeOpen} />
      <ServiceNotificationSendDialog serviceId={id} notification={toSend} open={!!toSend} onOpenChange={(o) => !o && setToSend(null)} />
    </>
  );
}
